import { t } from '../context/LocaleContext';
import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { useTheme } from '../context/ThemeContext';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  useLocale();
  const { locale } = useLocale();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const onFinish = async (values: {username: string;password: string;}) => {
    setLoading(true);
    try {
      await login(values.username, values.password);
      message.success(t('登录成功'));
      navigate('/');
    } catch (e: any) {
      const msg = e?.message || '';
      const retryMatch = msg.match(/还可尝试(\d+)次/);
      if (retryMatch) {
        message.error(t('密码错误，还可尝试__n__次').replace('__n__', retryMatch[1]));
      } else {
        message.error(t(msg) || t('登录失败'));
      }
    } finally {
      setLoading(false);
    }
  };

  const brandBg = 'linear-gradient(160deg, #072a4d 0%, #0B5394 40%, #1677ff 100%)';
  const cardBg = isDark ? '#1a1a2e' : '#ffffff';
  const pageBg = isDark ? '#0f0f1a' : '#f0f4f8';
  const textPrimary = isDark ? '#ffffff' : '#1a1a2e';
  const textSecondary = isDark ? 'rgba(255,255,255,0.5)' : '#6b7280';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';

  return (
    <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      background: pageBg,
      transition: 'background 0.3s ease'
    }}>

      {/* 左侧品牌区 - 桌面端 */}
      <div
      className="desktop-only"
      style={{
        flex: 1,
        background: brandBg,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '60px 48px',
        position: 'relative',
        overflow: 'hidden'
      }}>

        {/* 装饰光晕 */}
        <div
        style={{
          position: 'absolute',
          top: '-20%',
          left: '-10%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none'
        }} />

        <div
        style={{
          position: 'absolute',
          bottom: '-15%',
          right: '-5%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(26,95,180,0.4) 0%, transparent 70%)',
          filter: 'blur(80px)',
          pointerEvents: 'none'
        }} />


        {/* 顶部品牌 */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 20,
            background: 'rgba(255,255,255,0.12)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 40,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
          }}>

            <span style={{ fontSize: 36, fontWeight: 800, color: '#fff', letterSpacing: 2 }}>J</span>
          </div>
          <h1
          style={{
            color: '#fff',
            fontSize: 36,
            fontWeight: 700,
            margin: '0 0 12px 0',
            letterSpacing: 1
          }}>

            JDSUIT MTM
          </h1>
          <p
          style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: 'var(--font-lg)',
            margin: 0,
            lineHeight: 1.6,
            maxWidth: 380
          }}>

            {t('门店订单管理系统')}
          </p>

          {/* 分隔线 */}
          <div
          style={{
            width: 40,
            height: 3,
            background: 'linear-gradient(90deg, #fff, transparent)',
            borderRadius: 2,
            marginTop: 32
          }} />


          {/* 特性标签 */}
          <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
            { label: t('订单全流程管理'), desc: t('从下单到交付，一站式追踪') },
            { label: t('智能审核系统'), desc: t('多级审核，确保订单准确') },
            { label: t('数据实时同步'), desc: t('工厂、门店、物流信息实时更新') }].
            map((item, idx) =>
            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.6)',
                marginTop: 6,
                flexShrink: 0
              }} />

                <div>
                  <div style={{ color: '#fff', fontSize: 'var(--font-sm)', fontWeight: 500 }}>{item.label}</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 'var(--font-xs)', marginTop: 2 }}>{item.desc}</div>
                </div>
              </div>)}

          </div>
        </div>

        {/* 底部装饰 */}
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {[0.5, 0.3, 0.15].map((opacity, i) =>
            <div
            key={i}
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: `rgba(255,255,255,${opacity})`
            }} />)}


          </div>
          <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 'var(--font-xs)' }}>JDSUIT © 2025</span>
        </div>
      </div>

      {/* 右侧表单区 */}
      <div
      style={{
        width: '100%',
        maxWidth: 520,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
        position: 'relative'
      }}>

        {/* 表单卡片 */}
        <div
        style={{
          width: '100%',
          maxWidth: 400,
          background: cardBg,
          borderRadius: 24,
          padding: '48px 40px',
          boxShadow: isDark ?
          '0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)' :
          '0 20px 60px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.02)',
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)'
        }}>

          {/* 移动端品牌标识 */}
          <div className="mobile-only" style={{ textAlign: 'center', marginBottom: 32 }}>
            <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: 'linear-gradient(135deg, #1677ff, #0B5394)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>

              <span style={{ fontSize: 28, fontWeight: 700, color: '#fff' }}>J</span>
            </div>
            <h2 style={{ color: textPrimary, margin: 0, fontSize: 'var(--font-xl)', fontWeight: 700 }}>JDSUIT MTM</h2>
          </div>

          {/* 欢迎文案 */}
          <div style={{ marginBottom: 32 }}>
            <h2
            style={{
              color: textPrimary,
              fontSize: 28,
              fontWeight: 700,
              margin: '0 0 8px 0'
            }}>

              {t('欢迎回来')}
            </h2>
            <p style={{ color: textSecondary, fontSize: 'var(--font-sm)', margin: 0 }}>{t('请登录您的管理后台账户')}</p>
          </div>

          <Form onFinish={onFinish} autoComplete="off">
            <Form.Item
            name="username"
            rules={[{ required: true, message: t('请输入用户名') }]}
            style={{ marginBottom: 20 }}>

              <Input
              prefix={
              <UserOutlined style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#9ca3af', fontSize: 'var(--font-lg)' }} />}

              placeholder={t('请输入用户名')}
              size="large"
              style={{
                height: 48,
                background: inputBg,
                border: `1px solid ${inputBorder}`,
                borderRadius: 12,
                color: textPrimary,
                fontSize: 'var(--font-sm)',
                transition: 'all 0.2s ease'
              }} />

            </Form.Item>

            <Form.Item
            name="password"
            rules={[{ required: true, message: t('请输入密码') }]}
            style={{ marginBottom: 24 }}>

              <Input.Password
              prefix={
              <LockOutlined style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#9ca3af', fontSize: 'var(--font-lg)' }} />}

              placeholder={t('请输入密码')}
              size="large"
              style={{
                height: 48,
                background: inputBg,
                border: `1px solid ${inputBorder}`,
                borderRadius: 12,
                color: textPrimary,
                fontSize: 'var(--font-sm)',
                transition: 'all 0.2s ease'
              }} />

            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
              style={{
                height: 48,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #1677ff 0%, #0B5394 100%)',
                border: 'none',
                fontSize: 'var(--font-md)',
                fontWeight: 600,
                boxShadow: '0 4px 16px rgba(22,119,255,0.35)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.transform = 'translateY(-1px)';
                (e.target as HTMLElement).style.boxShadow = '0 6px 20px rgba(22,119,255,0.45)';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.transform = 'translateY(0)';
                (e.target as HTMLElement).style.boxShadow = '0 4px 16px rgba(22,119,255,0.35)';
              }}>

                {t('登录')}
              </Button>
            </Form.Item>
          </Form>

          {/* 底部版权 */}
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <p style={{ color: textSecondary, fontSize: 'var(--font-xs)', margin: 0 }}>
              JDSUIT © 2025 · {t('门店订单管理系统')}
            </p>
          </div>
        </div>
      </div>
      {locale === 'zh' && (
        <div style={{ position: 'fixed', bottom: 16, left: 24, fontSize: 11, color: '#bbb', pointerEvents: 'none', userSelect: 'none' }}>
          {t('沪ICP备2026036452号')}
        </div>
      )}
    </div>);

};

export default Login;